import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Tag, Edit2, Trash2, Plus } from 'lucide-react';
import { doc, onSnapshot, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import BlogPostEditorModal from '../components/BlogPostEditorModal';
import useConfirmation from '../hooks/useConfirmation';
import ConfirmationModal from '../components/ConfirmationModal';
import WhatsAppModal from '../components/WhatsAppModal';
import { BlogPostStructuredData } from '../components/StructuredData';
import { updateBlogPostsAggregationOnDelete, updateBlogPostsAggregationOnUpdate } from '../utils/blogPostsAggregations';
import useSEO from '../hooks/useSEO';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postForm, setPostForm] = useState({
    titulo: '',
    resumo: '',
    conteudo: '',
    urlImagem: '',
    etiquetas: ''
  });
  const [saving, setSaving] = useState(false);
  const { confirmation, showConfirmation, confirmAction, cancelAction } = useConfirmation();

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const postRef = doc(db, 'postsBlog', id);
    
    // Listener em tempo real
    const unsubscribe = onSnapshot(
      postRef,
      (postSnap) => {
        try {
          if (!postSnap.exists()) {
            setError('Post não encontrado');
            setPost(null);
            setLoading(false);
            return;
          }
          
          const postData = {
            id: postSnap.id,
            ...postSnap.data(),
            criadoEm: postSnap.data().criadoEm?.toDate?.() || new Date(postSnap.data().criadoEm)
          };
          
          setPost(postData);
          setLoading(false);
        } catch (error) {
          setError('Erro ao carregar o post');
          setLoading(false);
        }
      },
      (error) => {
        // Callback de erro do onSnapshot
        setError('Erro ao carregar o post');
        setLoading(false);
      }
    );

    // Cleanup: remover listener quando o componente desmontar ou o id mudar
    return () => {
      unsubscribe();
    };
  }, [id]);

  // SEO dinâmico: título e descrição com marca Aruk Dog para aparecer como tópico do site na pesquisa
  const seoTitle = post ? (post.titulo || post.title) : 'Post do Blog';
  const rawDescription = post ? (post.resumo || post.excerpt || post.titulo || post.title) : 'Leia mais no blog da Aruk Dog';
  const seoDescription = post && rawDescription
    ? `${rawDescription.replace(/\s*[|–—]\s*Aruk Dog\s*$/i, '').trim()} — Aruk Dog`
    : rawDescription;

  useSEO({
    title: seoTitle,
    description: seoDescription,
    canonical: post ? `/blog/${post.id}` : `/blog/${id}`,
    ogType: 'article',
    ogImage: post ? (post.urlImagem || post.imageUrl || '/Preto.png') : '/Preto.png'
  });

  const formatDate = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatReadingTime = (minutes) => {
    if (!minutes) return '2 min';
    return `${minutes} min de leitura`;
  };

  // Funções para editar/excluir post
  const handleOpenPostModal = useCallback(() => {
    if (post) {
      setEditingPost(post);
      setPostForm({
        titulo: post.titulo || post.title || '',
        resumo: post.resumo || post.excerpt || '',
        conteudo: post.conteudo || post.content || post.body || '',
        urlImagem: post.urlImagem || post.imageUrl || '',
        etiquetas: (post.etiquetas || post.tags) ? (post.etiquetas || post.tags).join(', ') : ''
      });
    }
    setShowPostModal(true);
  }, [post]);

  const handleClosePostModal = useCallback(() => {
    setShowPostModal(false);
    setEditingPost(null);
    setPostForm({
      titulo: '',
      resumo: '',
      conteudo: '',
      urlImagem: '',
      etiquetas: ''
    });
  }, []);

  const handleFormChange = useCallback((field, value) => {
    setPostForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSavePost = async () => {
    if (!postForm.titulo.trim()) {
      await showConfirmation({
        title: 'Campo obrigatório',
        message: 'O título é obrigatório.',
        confirmText: 'OK',
        showCancel: false,
        type: 'warning'
      });
      return;
    }

    const confirmSave = await showConfirmation({
      title: 'Salvar alterações',
      message: 'Deseja salvar as alterações neste post?',
      confirmText: 'Salvar',
      cancelText: 'Cancelar',
      type: 'warning'
    });

    if (!confirmSave) return;

    setSaving(true);
    try {
      const etiquetasArray = postForm.etiquetas
        ? postForm.etiquetas.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        : [];

      const postData = {
        titulo: postForm.titulo.trim(),
        resumo: postForm.resumo.trim() || '',
        conteudo: postForm.conteudo.trim() || '',
        urlImagem: postForm.urlImagem.trim() || '',
        etiquetas: etiquetasArray,
        atualizadoEm: Timestamp.now()
      };

      if (editingPost) {
        const updatedPostData = {
          ...postData,
          criadoEm: editingPost.criadoEm instanceof Date 
            ? Timestamp.fromDate(editingPost.criadoEm)
            : editingPost.criadoEm
        };
        
        await updateDoc(doc(db, 'postsBlog', editingPost.id), updatedPostData);
        
        // Para o agregado, manter os Timestamps originais do Firestore
        const postDataForAggregate = {
          id: editingPost.id,
          ...updatedPostData
        };
        
        // Atualizar agregado
        try {
          await updateBlogPostsAggregationOnUpdate(postDataForAggregate);
        } catch (err) {
          // Erro ao atualizar agregado
          // Erro ao atualizar agregado ao editar post
        }
        
        // O listener em tempo real atualizará automaticamente o post
        
        handleClosePostModal();
        
        // Mostrar confirmação após fechar o modal
        showConfirmation({
          title: 'Sucesso!',
          message: '',
          confirmText: 'OK',
          showCancel: false
        });
      }
    } catch (error) {
      await showConfirmation({
        title: 'Erro',
        message: 'Ocorreu um erro ao salvar o post. Por favor, tente novamente.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async () => {
    const confirmed = await showConfirmation({
      title: 'Excluir Post',
      message: 'Tem certeza que deseja excluir este post? Esta ação não pode ser desfeita.',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'postsBlog', post.id));
      
      // Atualizar agregado
      try {
        await updateBlogPostsAggregationOnDelete(post.id);
      } catch (err) {
        // Erro ao atualizar agregado
        // Erro ao atualizar agregado
      }
      
      await showConfirmation({
        title: 'Sucesso!',
        message: '',
        confirmText: 'OK',
        showCancel: false
      });
      navigate('/blog');
    } catch (error) {
      await showConfirmation({
        title: 'Erro',
        message: 'Ocorreu um erro ao excluir o post. Por favor, tente novamente.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    }
  };

  // Funções para o modal de WhatsApp
  const handleOpenWhatsAppModal = (e) => {
    if (e) {
      e.preventDefault();
    }
    setShowWhatsAppModal(true);
  };

  const BlogPostContent = () => {
    if (loading) {
      return (
        <div className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-muted-light dark:text-muted-dark">Carregando post...</p>
          </div>
        </div>
      );
    }

    if (error || !post) {
      return (
        <div className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold text-text-light dark:text-text-dark mb-4">
              {error || 'Post não encontrado'}
            </h2>
            <p className="text-muted-light dark:text-muted-dark mb-6">
              O post que você está procurando não existe ou foi removido.
            </p>
            <button
              onClick={() => navigate('/blog')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#5CC1B3' }}
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar</span>
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background-light dark:bg-background-dark">
        <BlogPostStructuredData post={post} />
        {/* Header */}
        <div 
          className="text-white py-4 sm:py-6 px-4 overflow-hidden"
          style={{ 
            backgroundColor: '#104347',
            backgroundImage: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }}
        >
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate('/blog')}
                className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Voltar</span>
              </button>
              <div className="flex-1 flex justify-center">
                <img 
                  src="/Branco.png" 
                  alt="Aruk Dog Logo" 
                  className="w-16 h-16 sm:w-20 sm:h-20"
                />
              </div>
              <button
                onClick={handleOpenWhatsAppModal}
                className="flex items-center gap-2 bg-white text-primary-600 px-4 py-2 rounded-lg font-semibold hover:bg-primary-50 transition-all duration-200 shadow-lg"
              >
                <Calendar className="w-5 h-5" />
                <span className="hidden sm:inline">Agendar</span>
                <span className="sm:hidden">Agendar</span>
              </button>
            </div>
          </div>
        </div>

        {/* Post Content */}
        <article className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
          {/* Post Header */}
          <header className="mb-8">
            <div className="flex items-center justify-between gap-4 text-sm text-muted-light dark:text-muted-dark mb-4">
              <div className="flex items-center gap-4">
                {post.criadoEm && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(post.criadoEm)}</span>
                  </div>
                )}
                {post.readingTime && (
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{formatReadingTime(post.readingTime)}</span>
                  </div>
                )}
              </div>
              {user && (
                <div className="flex gap-2">
                  <button
                    onClick={handleOpenPostModal}
                    className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    title="Editar post"
                    style={{ backgroundColor: '#5CC1B3' }}
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleDeletePost}
                    className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    title="Excluir post"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
            
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-text-light dark:text-text-dark mb-4">
              {post.titulo || post.title}
            </h1>
            
            {(post.resumo || post.excerpt) && (
              <p className="text-xl text-muted-light dark:text-muted-dark mb-6">
                {post.resumo || post.excerpt}
              </p>
            )}
            
          </header>

          {/* Featured Image */}
          {(post.urlImagem || post.imageUrl) && (
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={post.urlImagem || post.imageUrl}
                alt={post.titulo || post.title}
                className="w-full h-auto object-cover"
              />
            </div>
          )}

          {/* Post Body */}
          <div className="max-w-none">
            {(post.conteudo || post.content) ? (
              <div 
                className="text-text-light dark:text-text-dark leading-relaxed"
                style={{ 
                  fontporte: '1.125rem',
                  lineHeight: '1.75rem',
                  whiteSpace: 'pre-wrap'
                }}
                dangerouslySetInnerHTML={{ 
                  __html: post.conteudo || post.content
                }}
              />
            ) : (
              <p 
                className="text-text-light dark:text-text-dark leading-relaxed text-xl"
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {post.body || 'Conteúdo do post em breve...'}
              </p>
            )}
          </div>

          {/* Back to Blog Button and Agendar Serviço */}
          <div className="mt-12 pt-8 border-t border-border-light dark:border-border-dark flex justify-between items-center">
            <button
              onClick={() => navigate('/blog')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#5CC1B3' }}
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar</span>
            </button>
            <button
              onClick={handleOpenWhatsAppModal}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#5CC1B3' }}
            >
              <Calendar className="w-5 h-5" />
              <span>Agendar</span>
            </button>
          </div>
        </article>
      </div>
    );
  };

  // Se o usuário está logado, mostrar com Layout
  if (user) {
    return (
      <Layout>
        <BlogPostContent />
        <BlogPostEditorModal
          isOpen={showPostModal}
          editingPost={editingPost}
          postForm={postForm}
          onClose={handleClosePostModal}
          onSave={handleSavePost}
          onFormChange={handleFormChange}
          saving={saving}
        />
        <ConfirmationModal
          isOpen={confirmation.isOpen}
          title={confirmation.title}
          message={confirmation.message}
          confirmText={confirmation.confirmText}
          cancelText={confirmation.cancelText}
          type={confirmation.type}
          showCancel={confirmation.showCancel}
          onConfirm={confirmAction}
          onCancel={cancelAction}
        />
        {/* Modal de WhatsApp */}
        <WhatsAppModal 
          isOpen={showWhatsAppModal}
          onClose={() => setShowWhatsAppModal(false)}
        />
      </Layout>
    );
  }

  // Se não está logado, mostrar sem Layout
  return (
    <>
      <BlogPostContent />
      
      {/* Modal de Confirmação */}
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
        showCancel={confirmation.showCancel}
        onConfirm={confirmAction}
        onCancel={cancelAction}
      />
      
      {/* Modal de WhatsApp */}
      <WhatsAppModal 
        isOpen={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
      />
    </>
  );
};

export default BlogPost;
